import { ICollabOp, IDisplayItem, IScreenDesign, normalizeNodeId } from "@kuika/kuika-cl-model";

/** The only nodePayload field this module reads — the added subtree's root id. */
interface INodeAddPayloadRoot {
  id?: number | string;
}

/** The id of the node a structural op places: nodeMove's subject, nodeAdd's payload root. */
function movedNodeIdNorm(op: ICollabOp): string {
  if (op.kind === "nodeMove") {
    return normalizeNodeId(op.nodeId);
  }
  if (op.kind === "nodeAdd") {
    return normalizeNodeId((op.nodePayload as INodeAddPayloadRoot | undefined)?.id);
  }
  return "";
}

function findNode(nodes: IDisplayItem[], idNorm: string): IDisplayItem | null {
  for (const node of nodes) {
    if (normalizeNodeId(node.id) === idNorm) {
      return node;
    }
    if (Array.isArray(node.children)) {
      const found: IDisplayItem | null = findNode(node.children, idNorm);
      if (found !== null) {
        return found;
      }
    }
  }
  return null;
}

/** Mirrors sd-apply's parent resolution: "" = the screen root's displayItems array. */
function resolveSiblings(sd: IScreenDesign, parentIdNorm: string): IDisplayItem[] | null {
  if (parentIdNorm === "") {
    return sd.displayItems;
  }
  const parent: IDisplayItem | null = findNode(sd.displayItems, parentIdNorm);
  if (parent === null || !Array.isArray(parent.children)) {
    return null;
  }
  return parent.children;
}

/**
 * Where a structural op's subject node ACTUALLY sits in `sd` — read right after the op
 * was applied locally, this is the client's optimistic placement. The session core
 * compares the server ack's authoritative `resolvedIndex` against it and skips the
 * correction when they agree: the server placed the node exactly where this client
 * already did, so there is nothing to heal (KOA-18198 — a redundant correction applied
 * onto a tree that LATER local gestures had already changed transiently reverted them,
 * a user-visible flicker; it also forced a needless full re-render on every gesture).
 *
 * Returns null for non-structural ops and whenever the node/parent cannot be resolved —
 * null disables the skip and keeps the correction, the conservative side.
 */
export function sdOptimisticIndexOf(sd: IScreenDesign, op: ICollabOp): number | null {
  const idNorm: string = movedNodeIdNorm(op);
  if (idNorm === "") {
    return null;
  }
  const parentIdNorm: string = normalizeNodeId(op.parentId);
  const siblings: IDisplayItem[] | null = resolveSiblings(sd, parentIdNorm);
  if (siblings === null) {
    return null;
  }
  for (let i = 0; i < siblings.length; i++) {
    if (normalizeNodeId(siblings[i].id) === idNorm) {
      return i;
    }
  }
  return null;
}
